/**
 * APITestingKnowledgeCard — renders prior API testing knowledge
 * (what's done, what's pending, recommendations) inline in chat.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as PassedIcon,
  Error as FailedIcon,
  RadioButtonUnchecked as UntestedIcon,
} from '@mui/icons-material';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  high: '#f57c00',
  medium: '#fbc02d',
  low: '#1976d2',
};

const PRIORITY_ICONS: Record<string, { color: string; label: string }> = {
  open_finding: { color: '#d32f2f', label: 'P1' },
  untested: { color: '#f57c00', label: 'P2' },
  missing_security: { color: '#f57c00', label: 'P2' },
  missing_category: { color: '#1976d2', label: 'P3' },
  stale: { color: '#1976d2', label: 'P4' },
};

const METHOD_COLORS: Record<string, string> = {
  GET: '#4caf50',
  POST: '#ff9800',
  PUT: '#2196f3',
  DELETE: '#f44336',
  PATCH: '#9c27b0',
};

const MethodBadge: React.FC<{ method: string }> = ({ method }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      fontSize: '0.6rem',
      fontWeight: 700,
      color: '#fff',
      bgcolor: METHOD_COLORS[method] || '#757575',
      borderRadius: '3px',
      px: 0.5,
      py: 0.1,
      mr: 0.5,
      lineHeight: 1.3,
      fontFamily: 'monospace',
    }}
  >
    {method}
  </Box>
);

const CategoryBar: React.FC<{ label: string; percentage: number }> = ({ label, percentage }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
    <Typography variant="caption" sx={{ fontSize: '0.6rem', minWidth: 70, color: '#aaa' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, maxWidth: 80 }}>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': {
            bgcolor: percentage > 60 ? '#4caf50' : percentage > 30 ? '#ff9800' : '#f44336',
          },
        }}
      />
    </Box>
    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#888', minWidth: 28 }}>
      {percentage}%
    </Typography>
  </Box>
);

interface APITestingKnowledgeCardProps {
  data: Record<string, any>;
}

const APITestingKnowledgeCard: React.FC<APITestingKnowledgeCardProps> = ({ data }) => {
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const [recsExpanded, setRecsExpanded] = useState(true);

  const totalEndpoints = data.total_endpoints || 0;
  const testedEndpoints = data.tested_endpoints || 0;
  const untestedEndpoints = data.untested_endpoints || 0;
  const overallPassRate = data.overall_pass_rate || 0;
  const totalSessions = data.total_sessions || 0;
  const categoryCoverage = data.category_coverage || {};
  const testedSummary: any[] = data.tested_summary || [];
  const untestedSummary: any[] = data.untested_summary || [];
  const recommendations: any[] = data.recommendations || [];
  const openFindings = data.open_findings_count || 0;
  const criticalFindings = data.critical_findings_count || 0;

  // Compute "days ago" for last_session_at
  let lastTestedLabel = '';
  if (data.last_session_at) {
    const diff = Date.now() - new Date(data.last_session_at).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    lastTestedLabel = days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
  }

  const coveragePct = totalEndpoints > 0 ? Math.round((testedEndpoints / totalEndpoints) * 100) : 0;

  return (
    <Box
      sx={{
        bgcolor: 'rgba(25,118,210,0.06)',
        border: '1px solid rgba(25,118,210,0.2)',
        borderRadius: 1.5,
        p: 1.2,
        fontSize: '0.65rem',
        maxWidth: 520,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
          Prior API Testing Knowledge
        </Typography>
        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.6rem' }}>
          — {data.domain}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip label={`${totalSessions} session${totalSessions !== 1 ? 's' : ''}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
        {lastTestedLabel && (
          <Chip label={`Last: ${lastTestedLabel}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
        )}
        <Chip
          label={`${overallPassRate}% pass rate`}
          size="small"
          variant="outlined"
          color={overallPassRate >= 80 ? 'success' : overallPassRate >= 50 ? 'warning' : 'error'}
          sx={{ height: 18, fontSize: '0.6rem' }}
        />
        {openFindings > 0 && (
          <Chip
            label={`${openFindings} open finding${openFindings !== 1 ? 's' : ''}${criticalFindings > 0 ? ` (${criticalFindings} critical)` : ''}`}
            size="small"
            color="error"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.6rem' }}
          />
        )}
      </Box>

      {/* WHAT'S BEEN DONE */}
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        What's Been Done
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
          {testedEndpoints}/{totalEndpoints} endpoints tested
        </Typography>
        <Box sx={{ flex: 1, maxWidth: 120 }}>
          <LinearProgress
            variant="determinate"
            value={coveragePct}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                bgcolor: coveragePct > 70 ? '#4caf50' : coveragePct > 40 ? '#ff9800' : '#f44336',
              },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#888' }}>
          {coveragePct}%
        </Typography>
      </Box>

      {/* Category coverage */}
      <Box sx={{ mb: 0.8 }}>
        {Object.entries(categoryCoverage).map(([key, val]: [string, any]) => (
          <CategoryBar key={key} label={key.replace(/_/g, ' ')} percentage={val.percentage || 0} />
        ))}
      </Box>

      {/* Recently tested endpoints */}
      {testedSummary.length > 0 && (
        <Box sx={{ mb: 0.8 }}>
          {testedSummary.slice(0, 5).map((ep: any, i: number) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, py: 0.1 }}>
              {ep.last_status === 'passed' ? (
                <PassedIcon sx={{ fontSize: 12, color: '#4caf50' }} />
              ) : (
                <FailedIcon sx={{ fontSize: 12, color: '#f44336' }} />
              )}
              <MethodBadge method={ep.method} />
              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontFamily: 'monospace', flex: 1 }}>
                {ep.path}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888' }}>
                {ep.times_tested}x
              </Typography>
              {ep.open_findings > 0 && (
                <Chip
                  label={`${ep.open_findings} finding${ep.open_findings > 1 ? 's' : ''}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 14, fontSize: '0.5rem', ml: 0.3 }}
                />
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* WHAT'S PENDING */}
      {untestedEndpoints > 0 && (
        <>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mt: 0.5 }}
            onClick={() => setPendingExpanded(!pendingExpanded)}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              What's Pending — {untestedEndpoints} untested
            </Typography>
            <IconButton size="small" sx={{ p: 0 }}>
              {pendingExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>
          <Collapse in={pendingExpanded}>
            <List dense disablePadding sx={{ ml: 0.5 }}>
              {untestedSummary.map((ep: any, i: number) => (
                <ListItem key={i} disablePadding sx={{ py: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <UntestedIcon sx={{ fontSize: 10, color: '#888' }} />
                        <MethodBadge method={ep.method} />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', fontFamily: 'monospace' }}>
                          {ep.path}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}

      {/* RECOMMENDED NEXT ACTIONS */}
      {recommendations.length > 0 && (
        <>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mt: 0.5 }}
            onClick={() => setRecsExpanded(!recsExpanded)}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Recommended Next Actions
            </Typography>
            <IconButton size="small" sx={{ p: 0 }}>
              {recsExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>
          <Collapse in={recsExpanded}>
            <List dense disablePadding sx={{ ml: 0.5 }}>
              {recommendations.slice(0, 6).map((rec: any, i: number) => {
                const meta = PRIORITY_ICONS[rec.type] || { color: '#888', label: 'P?' };
                return (
                  <ListItem key={i} disablePadding sx={{ py: 0.1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Box
                            component="span"
                            sx={{
                              fontSize: '0.55rem',
                              fontWeight: 700,
                              color: meta.color,
                              minWidth: 18,
                            }}
                          >
                            {meta.label}
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                            {rec.title}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default APITestingKnowledgeCard;
